import * as Sentry from "@sentry/react";
import { useFetchAuthorizeNetCredentials } from "../../modules/auth/hooks/auth.queries";
import {
    AuthorizeNetSecureData,
    CardData,
    AcceptJSResponse,
} from "../../utils/types/authorize-net";
import useToast from "../useToast";
import { IAuthorizeNetCredentials } from "../../modules/auth/types/auth.types";

interface AcceptJS {
    dispatchData(
        secureData: AuthorizeNetSecureData,
        responseHandler: (response: AcceptJSResponse) => void
    ): void;
}

declare global {
    interface Window {
        Accept?: AcceptJS;
    }
}

const acceptJsUrl =
    import.meta.env.VITE_NODE_ENV === "production"
        ? "https://js.authorize.net/v1/Accept.js"
        : "https://jstest.authorize.net/v1/Accept.js";

let acceptJsPromise: Promise<AcceptJS> | null = null;

function loadAcceptJS() {
    if (acceptJsPromise) {
        return acceptJsPromise;
    }

    if (window.Accept) {
        return Promise.resolve(window.Accept);
    }

    acceptJsPromise = new Promise<AcceptJS>((resolve, reject) => {
        const script = document.createElement("script");
        let timeoutId: number;
        const cleanup = (readyHandler: () => void) => {
            window.clearTimeout(timeoutId);
            document.body.removeEventListener("handshake", readyHandler);
            script.onerror = null;
        };
        const handleReady = () => {
            const accept = window.Accept;

            if (!accept) {
                return;
            }

            cleanup(handleReady);
            resolve(accept);
        };
        const handleError = () => {
            reject(new Error("Failed to load Accept.js"));
            cleanup(handleReady);
            script.remove();
            Reflect.set(window, "Accept", undefined);
        };
        timeoutId = window.setTimeout(handleError, 10000);

        document.body.addEventListener("handshake", handleReady);
        script.src = acceptJsUrl;
        script.async = true;
        script.onerror = handleError;
        document.head.appendChild(script);
    }).catch((error: unknown) => {
        acceptJsPromise = null;
        throw error;
    });

    return acceptJsPromise;
}

export type AcceptJSValidationField =
    | "card_number"
    | "expiration_date"
    | "card_cvv"
    | "zip_code"
    | "cardholder_name";

export interface AcceptJSValidationIssue {
    code: string;
    field?: AcceptJSValidationField;
    message: string;
}

export class AcceptJSValidationError extends Error {
    issues: AcceptJSValidationIssue[];

    constructor(issues: AcceptJSValidationIssue[]) {
        super(
            issues[0]?.message ??
                "Please check your card details and try again."
        );
        this.name = "AcceptJSValidationError";
        this.issues = issues;
    }
}

export const isAcceptJSValidationError = (
    error: unknown
): error is AcceptJSValidationError => error instanceof AcceptJSValidationError;

const validationErrorMap: Record<
    string,
    { field?: AcceptJSValidationField; message: string }
> = {
    E_WC_04: {
        message: "Please check your card details and try again.",
    },
    E_WC_05: {
        field: "card_number",
        message: "Enter a valid card number.",
    },
    E_WC_06: {
        field: "expiration_date",
        message: "Enter a valid expiration month.",
    },
    E_WC_07: {
        field: "expiration_date",
        message: "Enter a valid expiration year.",
    },
    E_WC_08: {
        field: "expiration_date",
        message: "Enter an expiration date in the future.",
    },
    E_WC_15: {
        field: "card_cvv",
        message: "Enter a valid CVV.",
    },
    E_WC_16: {
        field: "zip_code",
        message: "Enter a valid zip code.",
    },
    E_WC_17: {
        field: "cardholder_name",
        message: "Enter a valid cardholder name.",
    },
};

const validationTextMatchers: {
    pattern: RegExp;
    field?: AcceptJSValidationField;
    message: string;
}[] = [
    {
        pattern: /card number/i,
        field: "card_number",
        message: "Enter a valid card number.",
    },
    {
        pattern: /expiration month/i,
        field: "expiration_date",
        message: "Enter a valid expiration month.",
    },
    {
        pattern: /expiration year/i,
        field: "expiration_date",
        message: "Enter a valid expiration year.",
    },
    {
        pattern: /expiration|expiry/i,
        field: "expiration_date",
        message: "Enter a valid expiration date.",
    },
    {
        pattern: /cvv|security code|card code/i,
        field: "card_cvv",
        message: "Enter a valid CVV.",
    },
    {
        pattern: /zip/i,
        field: "zip_code",
        message: "Enter a valid zip code.",
    },
    {
        pattern: /cardholder|name on card/i,
        field: "cardholder_name",
        message: "Enter a valid cardholder name.",
    },
];

const buildValidationIssue = (message: {
    code: string;
    text: string;
}): AcceptJSValidationIssue | null => {
    const validationError =
        validationErrorMap[message.code] ??
        validationTextMatchers.find((matcher) =>
            matcher.pattern.test(message.text)
        );

    if (!validationError) {
        return null;
    }

    const issue: AcceptJSValidationIssue = {
        code: message.code,
        message: validationError.message,
    };

    if (validationError.field) {
        issue.field = validationError.field;
    }

    return issue;
};

const buildAcceptJSValidationError = (
    response: AcceptJSResponse
): AcceptJSValidationError | null => {
    const issues = response.messages.message
        .map(buildValidationIssue)
        .filter((issue): issue is AcceptJSValidationIssue => issue !== null);

    if (issues.length === 0) {
        return null;
    }

    return new AcceptJSValidationError(issues);
};

const paymentUnavailableMessage =
    "Payment processing is unavailable. Please refresh and try again.";

type TokenizeFailureCause =
    "missing_credentials" | "script_unavailable" | "unmapped_response";

const reportTokenizeFailure = (
    cause: TokenizeFailureCause,
    response?: AcceptJSResponse
) => {
    Sentry.withScope((scope) => {
        scope.setTag("area", "accept-js");
        scope.setTag("tokenize_failure_cause", cause);
        scope.setContext("tokenization", {
            cause,
            result_code: response?.messages.resultCode ?? "none",
            message_codes:
                response?.messages.message
                    .map((message) => message.code)
                    .join(", ") || "none",
            message_texts:
                response?.messages.message
                    .map((message) => message.text)
                    .join(" | ") || "none",
        });
        Sentry.captureException(
            new Error(`AcceptJS tokenization failed: ${cause}`)
        );
    });
};

export const useAcceptJS = ({
    credentials,
    useDefaultCredentials = true,
}: {
    credentials?: IAuthorizeNetCredentials;
    useDefaultCredentials?: boolean;
} = {}) => {
    const { data } = useFetchAuthorizeNetCredentials(useDefaultCredentials);
    const { toast } = useToast();

    const clientKey =
        credentials?.public_client_key ?? data?.data.public_client_key;
    const apiLoginID = credentials?.api_login_id ?? data?.data.api_login_id;

    const tokenizeCard = async (
        cardData: CardData
    ): Promise<AcceptJSResponse> => {
        if (!clientKey || !apiLoginID) {
            const error = new Error(paymentUnavailableMessage);

            reportTokenizeFailure("missing_credentials");
            toast({ mode: "error", message: error.message });
            throw error;
        }

        let accept: AcceptJS;

        try {
            accept = await loadAcceptJS();
        } catch {
            const error = new Error(paymentUnavailableMessage);

            reportTokenizeFailure("script_unavailable");
            toast({ mode: "error", message: error.message });
            throw error;
        }

        const secureData: AuthorizeNetSecureData = {
            authData: { apiLoginID, clientKey },
            cardData: {
                cardNumber: cardData.cardNumber,
                month: cardData.month,
                year: cardData.year,
                cardCode: cardData.cardCode,
                zip: cardData.zip,
                fullName: cardData.fullName,
            },
        };

        return new Promise((resolve, reject) => {
            accept.dispatchData(secureData, (response: AcceptJSResponse) => {
                if (response.messages.resultCode === "Ok") {
                    resolve(response);
                    return;
                }

                const validationError = buildAcceptJSValidationError(response);

                if (validationError) {
                    reject(validationError);
                    return;
                }

                reportTokenizeFailure("unmapped_response", response);
                reject(new Error(paymentUnavailableMessage));
            });
        }).catch((error: unknown) => {
            if (isAcceptJSValidationError(error)) {
                throw error;
            }

            const message =
                error instanceof Error
                    ? error.message
                    : paymentUnavailableMessage;

            toast({ mode: "error", message });
            throw error;
        }) as Promise<AcceptJSResponse>;
    };

    return { tokenizeCard, isReady: !!clientKey && !!apiLoginID };
};
